/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { Autocomplete } from '@base-ui/react/autocomplete';
import cx from 'classnames';
import { Icon } from 'sarsaparilla';

import { getSuggestionLabel } from './QuickSearch.transforms';
import type { SearchSuggestionGroup, SearchSuggestionItem } from './QuickSearch.types';

import { IconBadge } from './IconBadge';

import styles from './SuggestionList.module.scss';

type SuggestionListProps = {
    onItemClick: (item: SearchSuggestionItem) => void;
};

/** Grouped suggestion rows rendered inside the quick-search autocomplete. */
export function SuggestionList({ onItemClick }: SuggestionListProps) {
    return (
        <Autocomplete.List className={styles.List}>
            {(group: SearchSuggestionGroup) => (
                <SuggestionGroup
                    key={group.groupType}
                    group={group}
                    onItemClick={onItemClick}
                />
            )}
        </Autocomplete.List>
    );
}

type SuggestionGroupProps = {
    group: SearchSuggestionGroup;
    onItemClick: (item: SearchSuggestionItem) => void;
};

function SuggestionGroup({ group, onItemClick }: SuggestionGroupProps) {
    return (
        <Autocomplete.Group items={group.items} className={styles.Group}>
            {group.label && (
                <Autocomplete.GroupLabel
                    className={cx(styles.GroupLabel, {
                        [styles.recent]: group.groupType === 'recent',
                    })}
                >
                    {group.label}
                </Autocomplete.GroupLabel>
            )}
            <Autocomplete.Collection>
                {(item: SearchSuggestionItem) => (
                    <SuggestionItem
                        key={`${item.id}-${item.orgId}`}
                        item={item}
                        onItemClick={onItemClick}
                    />
                )}
            </Autocomplete.Collection>
        </Autocomplete.Group>
    );
}

type SuggestionItemProps = {
    item: SearchSuggestionItem;
    onItemClick: (item: SearchSuggestionItem) => void;
};

function SuggestionItem({ item, onItemClick }: SuggestionItemProps) {
    const isQuery = item.type === 'query';
    // Rows that act on the palette itself rather than naming a place or thing
    const isCompact = isQuery || item.type === 'recent';

    return (
        <Autocomplete.Item
            value={item}
            className={cx(styles.Item, {
                'mt-2': item.type === 'clear-history',
            })}
            onClick={() => onItemClick(item)}
        >
            {item.type === 'clear-history' ? (
                <div className={styles.clearHistory}>
                    <Icon iconName="close-circle-outline" size="sm" />
                    <span className={styles.ItemLabel}>Clear search history</span>
                </div>
            ) : (
                <>
                    <IconBadge
                        iconName={item.icon}
                        size={isCompact ? 'sm' : 'md'}
                        isHistory={item.type === 'recent'}
                        isNearMe={item.type === 'near-me'}
                    />
                    <span className={styles.ItemTextGroup}>
                        <span className={styles.ItemLabel}>
                            {getSuggestionLabel(item)}
                        </span>
                        {item.description && (
                            <span className={styles.ItemType}>{item.description}</span>
                        )}
                    </span>
                </>
            )}
        </Autocomplete.Item>
    );
}
